import {
  gql,
  MutationHookOptions,
  QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client';
import {
  DELIVERY_UNIT_SALE_CATALOG_FIELDS,
  DELIVERY_UNIT_SALE_CATALOG_WITH_PLATFORM_PRODUCER_CONTRACT_FIELDS
} from './fragments/producer';
import { PLATFORM_PRODUCER_CONTRACT_FIELDS } from './fragments/contract';
import {
  GQLDeliveryUnitSaleCatalogWithPlatformProducerContract,
  GQLDeliveryUnitSaleCatalogWithPlatformProducerContractConnection,
  GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitAccessoryPricingCsvIntoCatalogArgs,
  GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitCurrentCompleteInventoryIntoCatalogArgs,
  GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitPiecePricingCsvIntoCatalogArgs,
  GQLMutationDeliveryUnitSaleCatalog_PublishDeliveryUnitSaleCatalogArgs,
  GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs,
  GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs,
  GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogsArgs,
  GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogWithPlatformProducerContractsArgs,
  GQLMutationDeliveryUnitSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs,
  GQLMutationDeliveryUnitSaleCatalog_DeleteDeliveryUnitSaleCatalogArgs,
  GQLMutationDeliveryUnitSaleCatalog_UpdateNameAndDescriptionArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { OperationsHelper } from './OperationHelper';
import { Exception } from '@jewelutions/core-common-domain';

export class DeliveryUnitSaleCatalogOperation {
  private queries = {
    DeliveryUnitSaleCatalog_exportDeliveryUnitAccessoryPricingCsvFromCatalog: gql`
      query exportDeliveryUnitAccessoryPricingCsvFromCatalog(
        $deliveryUnitSaleCatalogId: ID!
        $currency: Currency!
      ) {
        DeliveryUnitSaleCatalog_exportDeliveryUnitAccessoryPricingCsvFromCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
          currency: $currency
        )
      }
    `,
    DeliveryUnitSaleCatalog_exportDeliveryUnitPiecePricingCsvFromCatalog: gql`
      query exportDeliveryUnitPiecePricingCsvFromCatalog(
        $deliveryUnitSaleCatalogId: ID!
        $currency: Currency!
      ) {
        DeliveryUnitSaleCatalog_exportDeliveryUnitPiecePricingCsvFromCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
          currency: $currency
        )
      }
    `
  };

  deliveryUnitSaleCatalogWithPlatformProducerContractArrayFromResponse(
    data: any
  ): GQLDeliveryUnitSaleCatalogWithPlatformProducerContract[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(
      data,
      'deliveryUnitSaleCatalogWithPlatformProducerContracts'
    );
  }

  deliveryUnitSaleCatalogIdFromResponse(data: any): string {
    if (data.DeliveryUnitSaleCatalog_createFromDeliveryUnitSaleCatalog !== undefined) {
      return data.DeliveryUnitSaleCatalog_createFromDeliveryUnitSaleCatalog;
    }
    throw new Exception(`Cannot handle input data deliveryUnitSaleCatalogIdFromResponse`);
  }

  updateNameAndDescription(
    options: MutationHookOptions<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_UpdateNameAndDescriptionArgs
    > = {}
  ) {
    const mutation = gql`
      mutation DeliveryUnitSaleCatalog_updateNameAndDescription(
        $deliveryUnitSaleCatalogId: ID!
        $name: TranslatedStringInput!
        $description: TranslatedStringInput!
      ) {
        DeliveryUnitSaleCatalog_updateNameAndDescription(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
          name: $name
          description: $description
        )
      }
    `;
    return useMutation<Boolean, GQLMutationDeliveryUnitSaleCatalog_UpdateNameAndDescriptionArgs>(
      mutation,
      options
    );
  }

  createFromDeliveryUnitSaleCatalog(
    options: MutationHookOptions<
      String,
      GQLMutationDeliveryUnitSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation DeliveryUnitSaleCatalog_createFromDeliveryUnitSaleCatalog(
        $name: TranslatedStringInput!
        $description: TranslatedStringInput!
        $deliveryUnitSaleCatalogId: ID!
      ) {
        DeliveryUnitSaleCatalog_createFromDeliveryUnitSaleCatalog(
          name: $name
          description: $description
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
        )
      }
    `;
    return useMutation<String, GQLMutationDeliveryUnitSaleCatalog_CreateFromDeliveryUnitSaleCatalogArgs>(
      mutation,
      options
    );
  }

  importDeliveryUnitAccessoryPricingCsvIntoCatalog(
    options: MutationHookOptions<
      String,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitAccessoryPricingCsvIntoCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation importDeliveryUnitAccessoryPricingCsvIntoCatalog(
        $deliveryUnitSaleCatalogId: ID!
        $csv: Upload!
      ) {
        DeliveryUnitSaleCatalog_importDeliveryUnitAccessoryPricingCsvIntoCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
          csv: $csv
        )
      }
    `;
    return useMutation<
      String,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitAccessoryPricingCsvIntoCatalogArgs
    >(mutation, options);
  }

  importDeliveryUnitCurrentCompleteInventoryIntoCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitCurrentCompleteInventoryIntoCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation importDeliveryUnitCurrentCompleteInventoryIntoCatalog($deliveryUnitSaleCatalogId: ID!) {
        DeliveryUnitSaleCatalog_importDeliveryUnitCurrentCompleteInventoryIntoCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
        )
      }
    `;
    return useMutation<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitCurrentCompleteInventoryIntoCatalogArgs
    >(mutation, options);
  }

  publishDeliveryUnitSaleCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_PublishDeliveryUnitSaleCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation publishDeliveryUnitSaleCatalog($deliveryUnitSaleCatalogId: ID!) {
        DeliveryUnitSaleCatalog_publishDeliveryUnitSaleCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
        )
      }
    `;
    return useMutation<Boolean, GQLMutationDeliveryUnitSaleCatalog_PublishDeliveryUnitSaleCatalogArgs>(
      mutation,
      options
    );
  }

  deleteDeliveryUnitSaleCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_DeleteDeliveryUnitSaleCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation deleteDeliveryUnitSaleCatalog($deliveryUnitSaleCatalogId: ID!) {
        DeliveryUnitSaleCatalog_deleteDeliveryUnitSaleCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
        )
      }
    `;
    return useMutation<Boolean, GQLMutationDeliveryUnitSaleCatalog_DeleteDeliveryUnitSaleCatalogArgs>(
      mutation,
      options
    );
  }

  importDeliveryUnitPiecePricingCsvIntoCatalog(
    options: MutationHookOptions<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitPiecePricingCsvIntoCatalogArgs
    > = {}
  ) {
    const mutation = gql`
      mutation importDeliveryUnitPiecePricingCsvIntoCatalog($deliveryUnitSaleCatalogId: ID!, $csv: Upload!) {
        DeliveryUnitSaleCatalog_importDeliveryUnitPiecePricingCsvIntoCatalog(
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
          csv: $csv
        )
      }
    `;
    return useMutation<
      Boolean,
      GQLMutationDeliveryUnitSaleCatalog_ImportDeliveryUnitPiecePricingCsvIntoCatalogArgs
    >(mutation, options);
  }

  exportDeliveryUnitAccessoryPricingCsvFromCatalog(
    args: GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs,
    options: QueryHookOptions<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs
    > = {}
  ) {
    return useQuery<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs
    >(this.queries.DeliveryUnitSaleCatalog_exportDeliveryUnitAccessoryPricingCsvFromCatalog, {
      ...options,
      variables: args
    });
  }

  exportDeliveryUnitAccessoryPricingCsvFromCatalogLazy(
    options: QueryHookOptions<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs
    > = {}
  ) {
    return useLazyQuery<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitAccessoryPricingCsvFromCatalogArgs
    >(this.queries.DeliveryUnitSaleCatalog_exportDeliveryUnitAccessoryPricingCsvFromCatalog, { ...options });
  }

  exportDeliveryUnitPiecePricingCsvFromCatalog(
    args: GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs,
    options: QueryHookOptions<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs
    > = {}
  ) {
    return useQuery<String, GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs>(
      this.queries.DeliveryUnitSaleCatalog_exportDeliveryUnitPiecePricingCsvFromCatalog,
      { ...options, variables: args }
    );
  }

  exportDeliveryUnitPiecePricingCsvFromCatalogLazy(
    options: QueryHookOptions<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs
    > = {}
  ) {
    return useLazyQuery<
      String,
      GQLQueryDeliveryUnitSaleCatalog_ExportDeliveryUnitPiecePricingCsvFromCatalogArgs
    >(this.queries.DeliveryUnitSaleCatalog_exportDeliveryUnitPiecePricingCsvFromCatalog, { ...options });
  }

  getDeliveryUnitSaleCatalogs(
    args: GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogsArgs,
    options: QueryHookOptions<
      GQLDeliveryUnitSaleCatalogWithPlatformProducerContractConnection,
      GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogsArgs
    > = {}
  ) {
    const query = gql`
      ${PLATFORM_PRODUCER_CONTRACT_FIELDS}
      ${DELIVERY_UNIT_SALE_CATALOG_FIELDS}
      query getDeliveryUnitSaleCatalogs($producerPartyId: ID!, $deliveryUnitId: ID!) {
        DeliveryUnitSaleCatalog_getDeliveryUnitSaleCatalogs(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
        ) {
          cursor
          deliveryUnitSaleCatalogWithPlatformProducerContracts {
            deliveryUnitSaleCatalogPublished
            platformProducerContractActive
            platformProducerContract {
              ...PlatformProducerContractFields
            }
            deliveryUnitSaleCatalog {
              ...DeliveryUnitSaleCatalogFields
            }
          }
        }
      }
    `;
    return useQuery<
      GQLDeliveryUnitSaleCatalogWithPlatformProducerContractConnection,
      GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogsArgs
    >(query, { ...options, variables: args });
  }

  getDeliveryUnitSaleCatalogWithPlatformProducerContracts(
    args: GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogWithPlatformProducerContractsArgs,
    options: QueryHookOptions<
      GQLDeliveryUnitSaleCatalogWithPlatformProducerContractConnection,
      GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogWithPlatformProducerContractsArgs
    > = {}
  ) {
    const query = gql`
      ${DELIVERY_UNIT_SALE_CATALOG_WITH_PLATFORM_PRODUCER_CONTRACT_FIELDS}
      query getDeliveryUnitSaleCatalogWithPlatformProducerContracts($includeOnlyActiveContracts: Boolean!) {
        DeliveryUnitSaleCatalog_getDeliveryUnitSaleCatalogWithPlatformProducerContracts(
          includeOnlyActiveContracts: $includeOnlyActiveContracts
        ) {
          cursor
          hasMore
          deliveryUnitSaleCatalogWithPlatformProducerContracts {
            ...DeliveryUnitSaleCatalogWithPlatformProducerContractFields
          }
        }
      }
    `;
    return useQuery<
      GQLDeliveryUnitSaleCatalogWithPlatformProducerContractConnection,
      GQLQueryDeliveryUnitSaleCatalog_GetDeliveryUnitSaleCatalogWithPlatformProducerContractsArgs
    >(query, { ...options, variables: args });
  }
}
